import 'leaflet/dist/leaflet.css';
import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';



// ========== HELPER FUNCTIONS ========== //
const simplifyAddress = (complexAddress) => {
  let simplified = complexAddress
    .replace(/[A-Za-z]+ \d+\/\d+, \d+(st|nd|rd|th) Floor,?/i, "")
    .replace(/[A-Za-z]+ \d+,?/i, "")
    .replace(/Pvt\.? Ltd\.?/i, "")
    .replace(/\s+/g, " ")
    .trim();

  const isVadodara = simplified.toLowerCase().includes("vadodara");
  const isAhmedabad = simplified.toLowerCase().includes("ahmedabad");
  const isGandhinagar = simplified.toLowerCase().includes("gandhinagar");

  if (!isVadodara && !isAhmedabad && !isGandhinagar) {
    simplified += ", Ahmedabad, Gujarat";
  }

  return simplified;
};

const getCoordinatesFromGoogle = async (address, logCallback) => {
  const API_KEY = "AIzaSyDPSaUoXzy9Uq_V2_llukHOZGLec_PXBLU";
  const simplifiedAddress = simplifyAddress(address);
  logCallback(`[Geocoding] Trying Google Maps API for: ${simplifiedAddress}`);

  const encodedAddress = encodeURIComponent(simplifiedAddress);
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${API_KEY}`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    logCallback(`[Geocoding] Google Maps API Response:`, data);

    if (data.status === "OK" && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      logCallback(`[Geocoding] Success! Using Google Maps API.`);
      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        formattedAddress: data.results[0].formatted_address || simplifiedAddress,
        source: "Google Maps"
      };
    } else {
      logCallback(`[Geocoding] Google Maps API failed. Using fallback coordinates.`);
      if (simplifiedAddress.toLowerCase().includes("vadodara")) {
        return { latitude: 22.3072, longitude: 73.1812, formattedAddress: "Default: Vadodara", source: "Fallback" };
      } else if (simplifiedAddress.toLowerCase().includes("ahmedabad")) {
        return { latitude: 23.0225, longitude: 72.5714, formattedAddress: "Default: Ahmedabad", source: "Fallback" };
      } else {
        return { latitude: 23.0225, longitude: 72.5714, formattedAddress: "Default: Ahmedabad", source: "Fallback" };
      }
    }
  } catch (error) {
    console.error("[Geocoding] Error:", error);
    logCallback(`[Geocoding] Error: ${error.message}`);
    return { latitude: 23.0225, longitude: 72.5714, formattedAddress: "Default: Ahmedabad", source: "Error Fallback" };
  }
};

const fetchRouteMetrics = async (points) => {
  const url = "http://graphhopperapi.ethicsgroup.in/api/GraphHopper/route";
  const payload = { points, profile: "car", pointsEncoded: false, instructions: false, calcPoints: true };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.success && data.data.paths.length > 0) {
      const path = data.data.paths[0];
      return { distance: (path.distance / 1000).toFixed(2), time: (path.time / 3600000).toFixed(2) };
    }
    return { distance: 0, time: 0 };
  } catch (error) {
    console.error("Error fetching route metrics:", error);
    return { distance: 0, time: 0 };
  }
};

const fetchDirections = async (points) => {
  const url = "https://graphhopperapi.ethicsgroup.in/api/GraphHopper/route";
  const payload = { points, profile: "car", pointsEncoded: false, instructions: true, calcPoints: true };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.success && data.data.paths.length > 0) {
      return data.data.paths[0].instructions.map((instruction) => ({
        text: instruction.text,
        distance: instruction.distance ? `${(instruction.distance / 1000).toFixed(2)} km` : null,
        sign: instruction.sign,
        time: instruction.time ? `${Math.round(instruction.time / 1000)} sec` : null,
      }));
    }
    return [];
  } catch (error) {
    console.error("Error fetching directions:", error);
    return [];
  }
};

const getDirectionIcon = (sign) => {
  const icons = { 0: '↑', 1: '↗', 2: '→', 3: '↘', '-1': '↖', '-2': '←', '-3': '↙', 4: '🔄', 5: '✓', 6: '🏁', '-7': '↖' };
  return icons[sign] || '↑';
};

// ========== SHORT URL GENERATION LOGIC ========== //
const generateShortURL = (routeData) => {
  const routeString = JSON.stringify(routeData);
  const blob = new Blob([routeString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  // Store in localStorage with expiry (24 hours)
  const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000;
  const urlData = {
    url,
    expiry: expiryTime,
    routeData
  };
  localStorage.setItem(`route_${Date.now()}`, JSON.stringify(urlData));

  return url;
};

// ========== LIVE ROUTE MAP COMPONENT ========== //
const LiveRouteMap = ({ routes, isCustomerView = false }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const featureGroupRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [routeMetrics, setRouteMetrics] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (routes.length > 0 && mapRef.current && !mapInstance.current) {
      const map = L.map(mapRef.current).setView([routes[0].vehicle.coords[1], routes[0].vehicle.coords[0]], 12);
      mapInstance.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a>OpenStreetMaps</a>'
      }).addTo(map);

      const featureGroup = L.featureGroup().addTo(map);
      featureGroupRef.current = featureGroup;

      routes.forEach(async (route, routeIndex) => {
        setIsLoading(true);
        const points = [
          { latitude: route.vehicle.coords[1], longitude: route.vehicle.coords[0] },
          ...route.deliveries.map(d => ({ latitude: d.coords[1], longitude: d.coords[0] })),
        ];

        const metrics = await fetchRouteMetrics(points);
        setRouteMetrics(prev => ({ ...prev, [route.vehicle.name]: metrics }));

        L.marker([route.vehicle.coords[1], route.vehicle.coords[0]], {
          icon: L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/2838/2838912.png', iconSize: [32, 32], iconAnchor: [16, 32] }),
        }).addTo(featureGroup).bindTooltip(
          `<b>${route.vehicle.name}</b><br>Driver: ${route.vehicle.driver}<br>Capacity: ${route.vehicle.capacity}kg<br>Total Load: ${route.totalWeight}kg<br>Stops: ${route.deliveries.length}`,
          { permanent: false, direction: 'top', className: 'custom-tooltip' }
        );

        route.deliveries.forEach((delivery) => {
          L.marker([delivery.coords[1], delivery.coords[0]], {
            icon: L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', iconSize: [24, 24], iconAnchor: [12, 24] }),
          }).addTo(featureGroup).bindTooltip(
            `<b>${delivery.name}</b><br>Priority: ${delivery.priority}<br>Weight: ${delivery.weight}kg<br>Time: ${delivery.timeWindow.start}:00 - ${delivery.timeWindow.end}:00<br><strong>Vehicle:</strong> ${route.vehicle.name} (${route.vehicle.driver})`,
            { permanent: false, direction: 'top', className: 'custom-tooltip' }
          );
        });

        const path = points.map(point => [point.latitude, point.longitude]);
        const colors = ['blue', 'green', 'purple', 'orange', 'red'];
        const color = colors[routeIndex % colors.length];
        L.polyline(path, { color, weight: 4, opacity: 0.7 }).addTo(featureGroup).bindTooltip(
          `<b>Route for ${route.vehicle.name}</b><br>Total Distance: ${metrics.distance} km<br>Total Time: ${Math.floor(route.totalTime)} hrs ${Math.round((route.totalTime % 1) * 60)} mins<br>Stops: ${route.deliveries.length}`,
          { permanent: false, sticky: true, direction: 'center', className: 'custom-tooltip' }
        );
        setIsLoading(false);
      });
      setIsMapReady(true);
    }
  }, [routes]);

  useEffect(() => {
    if (isMapReady && featureGroupRef.current) {
      const checkBounds = setInterval(() => {
        if (featureGroupRef.current.getLayers().length > 0) {
          clearInterval(checkBounds);
          mapInstance.current.fitBounds(featureGroupRef.current.getBounds(), { padding: [50, 50] });
        }
      }, 100);
    }
  }, [isMapReady]);

  return (
    <>
      {isLoading && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          backgroundColor: 'white', padding: '15px 25px', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.2)', zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <div className="spinner" style={{
            width: '20px', height: '20px', border: '3px solid rgba(0,0,0,0.1)',
            borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite'
          }}></div>
          <span>Loading route data...</span>
        </div>
      )}
      <div ref={mapRef} style={{ height: '600px', width: '100%', position: 'relative' }} />
      {isCustomerView && routes[0] && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <h4 style={{ margin: '0 0 5px 0', color: '#1f2937' }}>Live Tracking</h4>
          <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
            Vehicle: {routes[0].vehicle.name} | Driver: {routes[0].vehicle.driver}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>
            This link will expire after delivery completion.
          </p>
        </div>
      )}
    </>
  );
};


// ========== CUSTOMER TRACKING PAGE ========== //
const CustomerTrackingPage = () => {
  const [routeData, setRouteData] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const routeKey = urlParams.get('route');

    if (routeKey) {
      const storedData = localStorage.getItem(routeKey);
      if (storedData) {
        const data = JSON.parse(storedData);
        if (data.expiry > new Date().getTime()) {
          setRouteData(data.routeData);
          setCustomerName(data.routeData.customer);
        } else {
          localStorage.removeItem(routeKey);
          alert("This tracking link has expired.");
        }
      } else {
        alert("Invalid or expired tracking link.");
      }
    }
    setLoading(false);
  }, []);

  if (loading) return <div>Loading...</div>;
  if (!routeData) return <div>No route data found.</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>📦 Live Tracking for {customerName}</h2>
      <LiveRouteMap routes={[routeData]} isCustomerView={true} />
    </div>
  );
};



// ========== DELIVERY MODAL (FIXED) ========== //
const DeliveryModal = ({ onClose, onSubmit, getCoordinates }) => {
  const [formData, setFormData] = useState({
    name: '', location: '', weight: '', priority: 'medium', timeStart: '', timeEnd: '', latitude: '', longitude: ''
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleLocationBlur = async () => {
    if (!formData.location) return;
    setIsGeocoding(true);
    const coords = await getCoordinates(formData.location, (message) => console.log(message));
    if (coords) {
      setFormData(prev => ({ ...prev, latitude: coords.latitude, longitude: coords.longitude }));
    }
    setIsGeocoding(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parseInt(formData.timeEnd) <= parseInt(formData.timeStart)) {
      alert('End time must be after start time!');
      return;
    }
    if (!formData.latitude || !formData.longitude) {
      alert("Please enter a valid location and wait for coordinates to auto-fill.");
      return;
    }
    onSubmit({
      ...formData,
      weight: parseInt(formData.weight),
      timeStart: parseInt(formData.timeStart),
      timeEnd: parseInt(formData.timeEnd)
    });
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{ display: 'block', position: 'fixed', zIndex: 1000, left: 0, top: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', margin: '5% auto', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Add Delivery</h3>
          <span onClick={onClose} style={{ color: '#9ca3af', fontSize: '28px', fontWeight: 'bold', cursor: 'pointer' }}>&times;</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Customer Name</label>
            <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Priya Distributors"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Location
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '5px' }}>
                (e.g., "Privilon Building, Iscon Cross Road, Ahmedabad" or "Sector 15, Gandhinagar")
              </span>
            </label>
            <input type="text" required value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              onBlur={handleLocationBlur} placeholder="Building, Street, City (e.g., Privilon Building, Iscon Cross Road, Ahmedabad)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            {isGeocoding && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#667eea' }}>
                <div className="spinner" style={{
                  width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.1)',
                  borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite'
                }}></div>
                Fetching coordinates...
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Latitude</label>
              <input type="text" readOnly value={formData.latitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Longitude</label>
              <input type="text" readOnly value={formData.longitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Weight (kg)</label>
              <input type="number" required min="1" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} placeholder="150"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Priority</label>
              <select required value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Time Start</label>
              <input type="number" required min="0" max="23" value={formData.timeStart} onChange={e => setFormData({ ...formData, timeStart: e.target.value })} placeholder="9"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Time End</label>
              <input type="number" required min="1" max="24" value={formData.timeEnd} onChange={e => setFormData({ ...formData, timeEnd: e.target.value })} placeholder="12"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={!formData.latitude || !formData.longitude} style={{
              flex: 1, padding: '12px', background: (!formData.latitude || !formData.longitude) ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: (!formData.latitude || !formData.longitude) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500
            }}>Add Delivery</button>
            <button type="button" onClick={onClose} style={{ padding: '12px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== VEHICLE MODAL (FIXED) ========== //
const VehicleModal = ({ onClose, onSubmit, getCoordinates }) => {
  const [formData, setFormData] = useState({
    name: '', type: 'car', driver: '', capacity: 300, location: '', latitude: '', longitude: ''
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleLocationBlur = async () => {
    if (!formData.location) return;
    setIsGeocoding(true);
    const coords = await getCoordinates(formData.location, (message) => console.log(message));
    if (coords) {
      setFormData(prev => ({ ...prev, latitude: coords.latitude, longitude: coords.longitude }));
    }
    setIsGeocoding(false);
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    let defaultCapacity = 300;
    if (type === 'bike') defaultCapacity = 100;
    if (type === 'truck') defaultCapacity = 500;
    if (type === 'van') defaultCapacity = 400;
    setFormData(prev => ({ ...prev, type, capacity: defaultCapacity }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.latitude || !formData.longitude) {
      alert("Please enter a valid location and wait for coordinates to auto-fill.");
      return;
    }
    onSubmit(formData);
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{ display: 'block', position: 'fixed', zIndex: 1000, left: 0, top: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', margin: '5% auto', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Add Vehicle</h3>
          <span onClick={onClose} style={{ color: '#9ca3af', fontSize: '28px', fontWeight: 'bold', cursor: 'pointer' }}>&times;</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Vehicle Number</label>
            <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., GJ-01-AB-1234"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Vehicle Type</label>
            <select value={formData.type} onChange={handleTypeChange}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
              <option value="bike">Bike</option>
              <option value="car">Car</option>
              <option value="truck">Truck</option>
              <option value="van">Van</option>
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Driver Name</label>
            <input type="text" required value={formData.driver} onChange={e => setFormData({ ...formData, driver: e.target.value })} placeholder="e.g., Ramesh Kumar"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Capacity (kg)</label>
            <input type="number" required min="10" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Location
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '5px' }}>
                (e.g., "Privilon Building, Iscon Cross Road, Ahmedabad" or "Sector 15, Gandhinagar")
              </span>
            </label>
            <input type="text" required value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              onBlur={handleLocationBlur} placeholder="Building, Street, City (e.g., Privilon Building, Iscon Cross Road, Ahmedabad)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            {isGeocoding && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#667eea' }}>
                <div className="spinner" style={{
                  width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.1)',
                  borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite'
                }}></div>
                Fetching coordinates...
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Latitude</label>
              <input type="text" readOnly value={formData.latitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Longitude</label>
              <input type="text" readOnly value={formData.longitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={!formData.latitude || !formData.longitude} style={{
              flex: 1, padding: '12px', background: (!formData.latitude || !formData.longitude) ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: (!formData.latitude || !formData.longitude) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500
            }}>Add Vehicle</button>
            <button type="button" onClick={onClose} style={{ padding: '12px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== EDIT DELIVERY MODAL (FIXED) ========== //
const EditDeliveryModal = ({ delivery, onClose, onSubmit, getCoordinates }) => {
  const [formData, setFormData] = useState({
    id: delivery.id,
    name: delivery.name,
    location: delivery.location,
    weight: delivery.weight,
    priority: delivery.priority,
    timeStart: delivery.timeWindow.start,
    timeEnd: delivery.timeWindow.end,
    latitude: delivery.coords[1],
    longitude: delivery.coords[0]
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleLocationBlur = async () => {
    if (!formData.location) return;
    setIsGeocoding(true);
    const coords = await getCoordinates(formData.location, (message) => console.log(message));
    if (coords) {
      setFormData(prev => ({ ...prev, latitude: coords.latitude, longitude: coords.longitude }));
    }
    setIsGeocoding(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (parseInt(formData.timeEnd) <= parseInt(formData.timeStart)) {
      alert('End time must be after start time!');
      return;
    }
    if (!formData.latitude || !formData.longitude) {
      alert("Please enter a valid location and wait for coordinates to auto-fill.");
      return;
    }
    onSubmit({
      ...formData,
      weight: parseInt(formData.weight),
      timeStart: parseInt(formData.timeStart),
      timeEnd: parseInt(formData.timeEnd)
    });
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{ display: 'block', position: 'fixed', zIndex: 1000, left: 0, top: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', margin: '5% auto', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Edit Delivery</h3>
          <span onClick={onClose} style={{ color: '#9ca3af', fontSize: '28px', fontWeight: 'bold', cursor: 'pointer' }}>&times;</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Customer Name</label>
            <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Priya Distributors"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Location
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '5px' }}>
                (e.g., "Privilon Building, Iscon Cross Road, Ahmedabad" or "Sector 15, Gandhinagar")
              </span>
            </label>
            <input type="text" required value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              onBlur={handleLocationBlur} placeholder="Building, Street, City (e.g., Privilon Building, Iscon Cross Road, Ahmedabad)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            {isGeocoding && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#667eea' }}>
                <div className="spinner" style={{
                  width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.1)',
                  borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite'
                }}></div>
                Fetching coordinates...
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Latitude</label>
              <input type="text" readOnly value={formData.latitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Longitude</label>
              <input type="text" readOnly value={formData.longitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Weight (kg)</label>
              <input type="number" required min="1" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} placeholder="150"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Priority</label>
              <select required value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Time Start</label>
              <input type="number" required min="0" max="23" value={formData.timeStart} onChange={e => setFormData({ ...formData, timeStart: e.target.value })} placeholder="9"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Time End</label>
              <input type="number" required min="1" max="24" value={formData.timeEnd} onChange={e => setFormData({ ...formData, timeEnd: e.target.value })} placeholder="12"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={!formData.latitude || !formData.longitude} style={{
              flex: 1, padding: '12px', background: (!formData.latitude || !formData.longitude) ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: (!formData.latitude || !formData.longitude) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500
            }}>Update Delivery</button>
            <button type="button" onClick={onClose} style={{ padding: '12px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== EDIT VEHICLE MODAL (FIXED) ========== //
const EditVehicleModal = ({ vehicle, onClose, onSubmit, getCoordinates }) => {
  const [formData, setFormData] = useState({
    id: vehicle.id,
    name: vehicle.name,
    type: vehicle.type,
    driver: vehicle.driver,
    capacity: vehicle.capacity,
    location: vehicle.location,
    latitude: vehicle.coords[1],
    longitude: vehicle.coords[0]
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleLocationBlur = async () => {
    if (!formData.location) return;
    setIsGeocoding(true);
    const coords = await getCoordinates(formData.location, (message) => console.log(message));
    if (coords) {
      setFormData(prev => ({ ...prev, latitude: coords.latitude, longitude: coords.longitude }));
    }
    setIsGeocoding(false);
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    let defaultCapacity = 300;
    if (type === 'bike') defaultCapacity = 100;
    if (type === 'truck') defaultCapacity = 500;
    if (type === 'van') defaultCapacity = 400;
    setFormData(prev => ({ ...prev, type, capacity: defaultCapacity }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.latitude || !formData.longitude) {
      alert("Please enter a valid location and wait for coordinates to auto-fill.");
      return;
    }
    onSubmit(formData);
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{ display: 'block', position: 'fixed', zIndex: 1000, left: 0, top: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', margin: '5% auto', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Edit Vehicle</h3>
          <span onClick={onClose} style={{ color: '#9ca3af', fontSize: '28px', fontWeight: 'bold', cursor: 'pointer' }}>&times;</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Vehicle Number</label>
            <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., GJ-01-AB-1234"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Vehicle Type</label>
            <select value={formData.type} onChange={handleTypeChange}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
              <option value="bike">Bike</option>
              <option value="car">Car</option>
              <option value="truck">Truck</option>
              <option value="van">Van</option>
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Driver Name</label>
            <input type="text" required value={formData.driver} onChange={e => setFormData({ ...formData, driver: e.target.value })} placeholder="e.g., Ramesh Kumar"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Capacity (kg)</label>
            <input type="number" required min="10" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Location
              <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '5px' }}>
                (e.g., "Privilon Building, Iscon Cross Road, Ahmedabad" or "Sector 15, Gandhinagar")
              </span>
            </label>
            <input type="text" required value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              onBlur={handleLocationBlur} placeholder="Building, Street, City (e.g., Privilon Building, Iscon Cross Road, Ahmedabad)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            {isGeocoding && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#667eea' }}>
                <div className="spinner" style={{
                  width: '14px', height: '14px', border: '2px solid rgba(0,0,0,0.1)',
                  borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite'
                }}></div>
                Fetching coordinates...
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Latitude</label>
              <input type="text" readOnly value={formData.latitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Longitude</label>
              <input type="text" readOnly value={formData.longitude} placeholder="Auto-filled"
                style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#f3f4f6' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={!formData.latitude || !formData.longitude} style={{
              flex: 1, padding: '12px', background: (!formData.latitude || !formData.longitude) ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: (!formData.latitude || !formData.longitude) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500
            }}>Update Vehicle</button>
            <button type="button" onClick={onClose} style={{ padding: '12px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== DASHBOARD TAB (UPDATED) ========== //
const DashboardTab = ({ metrics, optimizedRoutes }) => {
  const [copiedRouteId, setCopiedRouteId] = useState(null);

  // DashboardTab.jsx
// DashboardTab.jsx
const generateShortURL = (routeData, customerName) => {
  const customerRouteData = {
    vehicle: routeData.vehicle,
    customer: customerName,
    deliveries: routeData.deliveries.filter(d => d.name === customerName),
  };

  const routeString = JSON.stringify(customerRouteData);
  const blob = new Blob([routeString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  // Replace spaces with underscores for localStorage key
  const storageKey = `route_${Date.now()}_${customerName.replace(/\s+/g, '_').replace(/[&]/g, '')}`;
  const urlData = {
    url,
    routeData: customerRouteData,
  };

  localStorage.setItem(storageKey, JSON.stringify(urlData));

  // Encode the storageKey for URL
  return `${window.location.origin}/customer_tracking?route=${encodeURIComponent(storageKey)}`;
};



  const handleCopyURL = (route, customerName) => {
    const longURL = generateShortURL(route, customerName);
    navigator.clipboard.writeText(longURL);
    alert(`Tracking link for ${customerName} copied to clipboard!`);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {[
          { label: 'Total Distance', icon: '📍', value: metrics.totalDistance, change: '↓ 23% less than manual' },
          { label: 'Estimated Time', icon: '⏱️', value: metrics.totalTime, change: '↓ 35% time saved' },
          { label: 'Fuel Cost', icon: '⛽', value: metrics.totalCost, change: '↓ 28% cost reduction' },
          { label: 'Vehicles Used', icon: '🚛', value: metrics.vehiclesUsed, change: metrics.optimization }
        ].map((metric, idx) => (
          <div key={idx} style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>{metric.label}</span>
              <div style={{ width: '40px', height: '40px', background: '#ede9fe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                {metric.icon}
              </div>
            </div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#1f2937', marginBottom: '5px' }}>{metric.value}</div>
            <div style={{ fontSize: '14px', color: '#10b981', fontWeight: 500 }}>{metric.change}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>Live Route Tracking</h3>
        {optimizedRoutes.length > 0 ? (
          <>
            <LiveRouteMap routes={optimizedRoutes} />
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '10px' }}>Shareable Tracking Links (Expires after delivery)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
                {optimizedRoutes.map((route, routeIndex) => (
                  <div key={routeIndex} style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{route.vehicle.name} ({route.vehicle.driver})</p>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: '5px 0 0 0' }}>📦 {route.deliveries.length} stops | ⚖️ {route.totalWeight}kg</p>
                      </div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      {route.deliveries.map((delivery, deliveryIndex) => (
                        <div key={deliveryIndex} style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px' }}>{delivery.name}</span>
                          <button
                            onClick={() => handleCopyURL(route, delivery.name)}
                            style={{
                              padding: '6px 10px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            🔗 Copy Link
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div style={{ background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: '12px', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
              </svg>
              <p style={{ marginTop: '15px', fontSize: '16px', color: '#1f2937' }}>Interactive Map View</p>
              <p style={{ fontSize: '14px', marginTop: '5px', color: '#6b7280' }}>Ahmedabad Region - Gujarat</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ========== DELIVERIES TAB ========== //
const DeliveriesTab = ({ deliveries, onAddDelivery, onRemoveDelivery, onEditDelivery }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
      <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Today's Deliveries</h3>
      <button onClick={onAddDelivery} style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>+ Add Delivery</button>
    </div>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {deliveries.map(d => (
        <li key={d.id} style={{ padding: '15px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ fontSize: '16px', color: '#1f2937', margin: 0 }}>{d.name}</h4>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '5px 0' }}>📍 {d.location}</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>⚖️ Weight: {d.weight}kg | ⏰ Time: {d.timeWindow.start}:00 - {d.timeWindow.end}:00</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>🌐 Lat: {d.coords[1]}, Long: {d.coords[0]}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, background: d.priority === 'high' ? '#fee2e2' : d.priority === 'medium' ? '#fef3c7' : '#dbeafe', color: d.priority === 'high' ? '#991b1b' : d.priority === 'medium' ? '#92400e' : '#1e40af' }}>
              {d.priority.charAt(0).toUpperCase() + d.priority.slice(1)}
            </span>
            <button
              onClick={() => onEditDelivery(d)}
              style={{
                minWidth: '90px',
                padding: '8px 12px',
                background: '#fbbf24',
                color: 'black',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => onRemoveDelivery(d.id)}
              style={{
                minWidth: '90px',
                padding: '8px 12px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              🗑️ Delete
            </button>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// ========== VEHICLES TAB ========== //
const VehiclesTab = ({ vehicles, onAddVehicle, onRemoveVehicle, onToggleVehicle, onEditVehicle }) => {
  const ToggleSwitch = ({ checked, onChange }) => (
    <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, background: checked ? '#10b981' : '#ccc', transition: '.4s', borderRadius: '24px' }}>
        <span style={{ position: 'absolute', height: '18px', width: '18px', left: checked ? '26px' : '3px', bottom: '3px', background: 'white', transition: '.4s', borderRadius: '50%', content: '""' }}></span>
      </span>
    </label>
  );

  const getVehicleIcon = (type) => {
    switch(type.toLowerCase()) {
      case 'bike': return '🏍️';
      case 'car': return '🚗';
      case 'truck': return '🚛';
      case 'van': return '🚐';
      default: return '🚚';
    }
  };

  const getVehicleColor = (type) => {
    switch(type.toLowerCase()) {
      case 'bike': return '#3b82f6';
      case 'car': return '#10b981';
      case 'truck': return '#ef4444';
      case 'van': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Fleet Management</h3>
        <button onClick={onAddVehicle} style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>+ Add Vehicle</button>
      </div>
      <div style={{ display: 'grid', gap: '15px' }}>
        {vehicles.map(v => (
          <div key={v.id} style={{
            padding: '12px',
            border: '2px solid #e5e7eb',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: v.status === 'unavailable' ? 0.6 : 1,
            borderLeft: `4px solid ${getVehicleColor(v.type)}`
          }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '16px', color: getVehicleColor(v.type), margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getVehicleIcon(v.type)} <span>{v.name}</span>
              </h4>
              <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Driver: {v.driver}</p>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Type: {v.type}</p>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Capacity: {v.capacity}kg</p>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '5px 0 0 0' }}>📍 {v.location}</p>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>🌐 Lat: {v.coords[1]}, Long: {v.coords[0]}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
              <span style={{ color: v.status === 'available' ? '#10b981' : '#ef4444', fontWeight: 600 }}>● {v.status.charAt(0).toUpperCase() + v.status.slice(1)}</span>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => onEditVehicle(v)}
                  style={{
                    minWidth: '80px',
                    padding: '6px 10px',
                    background: '#fbbf24',
                    color: 'black',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  ✏️ Edit
                </button>
                <ToggleSwitch checked={v.status === 'available'} onChange={() => onToggleVehicle(v.id)} />
                <button
                  onClick={() => onRemoveVehicle(v.id)}
                  style={{
                    minWidth: '80px',
                    padding: '6px 10px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== ALGORITHM LOGIC TAB ========== //
const AlgorithmLogicTab = ({ vehicleTypes, priorityConfig, algorithmParams, onSaveConfig, onResetConfig }) => {
  const [localPriority, setLocalPriority] = useState(priorityConfig);
  const [localAlgorithmParams, setLocalAlgorithmParams] = useState(algorithmParams);
  const [localAllowedTypes, setLocalAllowedTypes] = useState(vehicleTypes);

  const handlePriorityChange = (index, direction) => {
    const newPriority = [...localPriority];
    [newPriority[index], newPriority[index + direction]] = [newPriority[index + direction], newPriority[index]];
    setLocalPriority(newPriority);
  };

  const handleToggleVehicleType = (type) => {
    setLocalAllowedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const handleParamChange = (key, value) => {
    setLocalAlgorithmParams(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSaveConfig({ priority: localPriority, allowedTypes: localAllowedTypes, params: localAlgorithmParams });
  };

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Algorithm Logic Configuration</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onResetConfig} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Reset</button>
          <button onClick={handleSubmit} style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>Save</button>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '15px' }}>Vehicle Priority</h4>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>Set priority order for vehicle selection.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {localPriority.map((vehicle, index) => (
            <div key={vehicle} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>{index + 1}. {vehicle}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '5px' }}>
                {index > 0 && <button onClick={() => handlePriorityChange(index, -1)} style={{ padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>↑</button>}
                {index < localPriority.length - 1 && <button onClick={() => handlePriorityChange(index, 1)} style={{ padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>↓</button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '15px' }}>Allowed Vehicle Types</h4>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>Toggle to allow/disallow vehicle types for optimization.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          {['bike', 'car', 'truck', 'van'].map(type => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
              <label style={{ fontSize: '14px', color: '#1f2937', cursor: 'pointer', textTransform: 'capitalize' }}>{type}</label>
              <input type="checkbox" checked={localAllowedTypes.includes(type)} onChange={() => handleToggleVehicleType(type)} style={{ marginLeft: 'auto' }} />
            </div>
          ))}
        </div>
      </div>

      {/* <div style={{ marginBottom: '30px' }}>
        <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '15px' }}>Algorithm Parameters</h4>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>Configure parameters for route optimization.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
          {Object.entries(localAlgorithmParams).map(([key, value]) => (
            <div key={key} style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </label>
              <input
                type={typeof value === 'number' ? 'number' : 'text'}
                value={value}
                onChange={(e) => handleParamChange(key, typeof value === 'number' ? parseFloat(e.target.value) : e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

// ========== OPTIMIZATION TAB ========== //
const OptimizationTab = ({ warningMessage, optimizationStep, isOptimizing, showOptimizedRoutes, optimizedRoutes, onStartOptimization, onResetOptimization, onViewRouteDetails }) => (
  <div>
    {warningMessage && (
      <div style={{ padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <span>{warningMessage}</span>
      </div>
    )}

    <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Route Optimization Engine</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showOptimizedRoutes && (
            <button onClick={onResetOptimization} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>🔄 Reset</button>
          )}
          {!showOptimizedRoutes && (
            <button onClick={onStartOptimization} disabled={isOptimizing} style={{
              padding: '10px 20px', background: isOptimizing ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px',
              cursor: isOptimizing ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              {isOptimizing ? 'Calling API...' : '▶️ Start Optimization'}
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px' }}>
        <h4 style={{ marginBottom: '15px', color: '#1f2937' }}>Optimization Process</h4>
        {[
          'Checking vehicle availability and capacity',
          'Analyzing delivery priorities and time windows',
          'Calculating optimal distances via GraphHopper',
          'Distributing workload across available vehicles',
          'Generating optimized routes'
        ].map((text, idx) => {
          const stepNum = idx + 1;
          const isActive = optimizationStep === stepNum;
          const isCompleted = optimizationStep > stepNum || (stepNum === 5 && showOptimizedRoutes);
          return (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: '15px', padding: '12px', marginBottom: '10px', borderRadius: '8px', background: 'white',
              opacity: isCompleted || isActive ? 1 : 0.4, boxShadow: isActive ? '0 2px 8px rgba(102, 126, 234, 0.2)' : 'none',
              borderLeft: isCompleted ? '4px solid #10b981' : isActive ? '4px solid #667eea' : '4px solid transparent'
            }}>
              <div style={{
                width: '35px', height: '35px', background: isCompleted ? '#10b981' : '#667eea', color: 'white', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600
              }}>
                {isCompleted ? '✓' : stepNum}
              </div>
              <div style={{ flex: 1, fontSize: '15px', color: '#1f2937' }}>{text}</div>
            </div>
          );
        })}
      </div>

      {showOptimizedRoutes && optimizedRoutes.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '15px', color: '#1f2937' }}>Optimized Routes (via API)</h4>
          {optimizedRoutes.map((route, idx) => (
            <div key={idx} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '15px', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>🚚 {route.vehicle.name} - {route.vehicle.driver}</div>
                <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 500 }}>Optimized</span>
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <h5 style={{ fontSize: '14px', marginBottom: '10px', opacity: 0.9 }}>Route ({route.deliveries.length} stops)</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  {route.routeSequence.map((location, i) => (
                    <React.Fragment key={i}>
                      <span style={{ background: 'white', color: '#667eea', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>{location}</span>
                      {i < route.routeSequence.length - 1 && <span>→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <p style={{ marginTop: '10px', fontSize: '13px', opacity: 0.9 }}>Load: {route.totalWeight}kg / {route.vehicle.capacity}kg</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '5px' }}>Distance</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>{route.totalDistance} km</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '5px' }}>Time</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>{Math.floor(route.totalTime)} hrs {Math.round((route.totalTime % 1) * 60)} mins</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '5px' }}>Cost</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>₹{route.totalCost}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '5px' }}>Stops</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>{route.deliveries.length}</div>
                </div>
              </div>
              <button
                onClick={() => onViewRouteDetails(route)}
                style={{ marginTop: '15px', padding: '10px 20px', background: 'white', color: '#667eea', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, width: '100%' }}
              >
                View Route Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ========== ROUTE ANALYSIS MODAL ========== //
const RouteAnalysisModal = ({ route, instructions, loading, onClose }) => {
  if (!route) return null;
  return (
    <div onClick={onClose} style={{ display: 'block', position: 'fixed', zIndex: 1000, left: 0, top: 0, width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.6)', overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', margin: '2% auto', borderRadius: '16px', width: '95%', maxWidth: '1200px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '25px 30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '8px' }}>📊 Detailed Route Analysis</h2>
              <p style={{ fontSize: '14px', margin: 0, opacity: 0.9 }}>{route.vehicle.name} - {route.vehicle.driver}</p>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '28px', width: '45px', height: '45px', borderRadius: '50%', cursor: 'pointer' }}>×</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '30px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
            {[
              { icon: '📍', label: 'Total Distance', value: `${route.totalDistance} km`, color: '#667eea' },
              { icon: '⏱️', label: 'Estimated Time', value: `${Math.floor(route.totalTime)} hrs ${Math.round((route.totalTime % 1) * 60)} mins`, color: '#10b981' },
              { icon: '💰', label: 'Fuel Cost', value: `₹${route.totalCost}`, color: '#f59e0b' },
              { icon: '📦', label: 'Total Stops', value: route.deliveries.length, color: '#ef4444' },
              { icon: '⚖️', label: 'Total Load', value: `${route.totalWeight}kg`, color: '#8b5cf6' },
              { icon: '🚚', label: 'Capacity Used', value: `${Math.round((route.totalWeight / route.vehicle.capacity) * 100)}%`, color: '#ec4899' }
            ].map((stat, idx) => (
              <div key={idx} style={{ background: 'white', border: '2px solid #f3f4f6', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{stat.icon}</div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '5px' }}>{stat.label}</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              </div>
            ))}
          </div>

          <div style={{ background: 'white', border: '2px solid #f3f4f6', borderRadius: '12px', padding: '25px', marginBottom: '25px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>🗺️ Complete Route Sequence</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ minWidth: '60px', height: '60px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px' }}>🏭</div>
                <div style={{ flex: 1, background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '10px', padding: '15px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>Starting Point - Depot</div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '5px' }}>📍 {route.vehicle.location}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>🌐 {route.vehicle.coords[1].toFixed(6)}, {route.vehicle.coords[0].toFixed(6)}</div>
                </div>
              </div>
              <div style={{ marginLeft: '30px', fontSize: '24px', color: '#9ca3af' }}>↓</div>
              {route.deliveries.map((delivery, idx) => (
                <React.Fragment key={idx}>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <div style={{
                      minWidth: '60px', height: '60px',
                      background: delivery.priority === 'high' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                                 delivery.priority === 'medium' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                                 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white'
                    }}>
                      <div style={{ fontSize: '20px' }}>📦</div>
                      <div style={{ fontSize: '11px', fontWeight: 600 }}>Stop {idx + 1}</div>
                    </div>
                    <div style={{ flex: 1, background: 'white', border: '2px solid #e5e7eb', borderRadius: '10px', padding: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>{delivery.name}</div>
                        <span style={{
                          padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                          background: delivery.priority === 'high' ? '#fee2e2' : delivery.priority === 'medium' ? '#fef3c7' : '#dbeafe',
                          color: delivery.priority === 'high' ? '#991b1b' : delivery.priority === 'medium' ? '#92400e' : '#1e40af'
                        }}>
                          {delivery.priority.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '5px' }}>📍 {delivery.location}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>⚖️ {delivery.weight}kg | ⏰ {delivery.timeWindow.start}:00 - {delivery.timeWindow.end}:00</div>
                    </div>
                  </div>
                  {idx < route.deliveries.length - 1 && <div style={{ marginLeft: '30px', fontSize: '24px', color: '#9ca3af' }}>↓</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ background: 'white', border: '2px solid #f3f4f6', borderRadius: '12px', padding: '25px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '20px' }}>🧭 Turn-by-Turn Navigation</h3>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ width: '50px', height: '50px', border: '4px solid #f3f4f6', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 15px' }}/>
                <p style={{ color: '#6b7280' }}>Fetching route instructions...</p>
              </div>
            ) : instructions.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {instructions.map((instruction, idx) => (
                  <div key={idx} style={{
                    display: 'flex', gap: '15px', padding: '15px', background: idx % 2 === 0 ? '#f9fafb' : 'white', borderRadius: '8px', marginBottom: '10px'
                  }}>
                    <div style={{ minWidth: '40px', height: '40px', background: '#667eea', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600 }}>{idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>{instruction.text}</div>
                      {instruction.distance && <div style={{ fontSize: '13px', color: '#6b7280' }}>📏 {instruction.distance}</div>}
                      {instruction.time && <div style={{ fontSize: '13px', color: '#6b7280' }}>⏱️ {instruction.time}</div>}
                    </div>
                    <div style={{ fontSize: '24px' }}>{getDirectionIcon(instruction.sign)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>🗺️</div>
                <p>No instructions available. Check your connection or route.</p>
              </div>
            )}
          </div>
        </div>
        <div style={{ borderTop: '2px solid #f3f4f6', padding: '20px 30px', background: '#f9fafb', display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>💡 Based on optimized routing algorithms</div>
          <button onClick={onClose} style={{ padding: '12px 30px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ========== //
const RouteOptimizationSystem = () => {
  const API_BASE_URL = 'https://routeoptimizationtestapi.ethicsgroup.in/api';
  const [activeTab, setActiveTab] = useState('dashboard');
  const [deliveries, setDeliveries] = useState([
    { id: 1, name: 'Customer A - Priya Distributors', location: 'Sector 15, Gandhinagar', priority: 'high', time: '9:00 AM', weight: 150, coords: [72.6372917, 23.2355469], timeWindow: { start: 9, end: 11 } },
    { id: 2, name: 'Customer B - Royal Enterprises', location: 'S.G. Highway, Ahmedabad', priority: 'medium', time: '10:30 AM', weight: 200, coords: [72.5200008, 23.0582594], timeWindow: { start: 10, end: 13 } },
    { id: 3, name: 'Customer C - Mehta & Sons', location: 'Vastrapur, Ahmedabad', priority: 'high', time: '11:00 AM', weight: 180, coords: [72.5277849, 23.0376238], timeWindow: { start: 11, end: 14 } },
    { id: 4, name: 'Customer D - City Traders', location: 'Satellite, Ahmedabad', priority: 'low', time: '2:00 PM', weight: 120, coords: [72.5178289, 23.0301227], timeWindow: { start: 14, end: 17 } },
    { id: 5, name: 'Customer E - Patel Wholesale', location: 'Maninagar, Ahmedabad', priority: 'medium', time: '3:30 PM', weight: 160, coords: [72.604097, 22.995165], timeWindow: { start: 15, end: 18 } }
  ]);

  const [vehicles, setVehicles] = useState([
    { id: 1, name: 'Vehicle 001', type: 'truck', driver: 'Ramesh Kumar', capacity: 500, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] },
    { id: 2, name: 'Vehicle 002', type: 'car', driver: 'Suresh Patel', capacity: 300, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] },
    { id: 3, name: 'Vehicle 003', type: 'bike', driver: 'Mahesh Shah', capacity: 100, location: 'Aslali, Ahmedabad', status: 'available', coords: [72.5970569, 22.9146173] }
  ]);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationStep, setOptimizationStep] = useState(0);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [showOptimizedRoutes, setShowOptimizedRoutes] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showRouteAnalysisModal, setShowRouteAnalysisModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [apiError, setApiError] = useState('');
  const [metrics, setMetrics] = useState({
    totalDistance: '77 km', totalTime: '4.3 hrs', totalCost: '₹770', vehiclesUsed: '2/3', optimization: '33% resource optimization'
  });
  const [shortURLs, setShortURLs] = useState({});

  // ===== EDIT MODALS STATE ===== //
  const [showEditDeliveryModal, setShowEditDeliveryModal] = useState(false);
  const [showEditVehicleModal, setShowEditVehicleModal] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);

  // ===== ALGORITHM LOGIC STATE ===== //
  const [vehicleTypes, setVehicleTypes] = useState(['bike', 'car', 'truck']);
  const [priorityConfig, setPriorityConfig] = useState(['bike', 'car', 'truck']);
  const [algorithmParams, setAlgorithmParams] = useState({
    maxDistance: 500, maxTime: 8, maxStops: 20, costPerKM: 10, fuelEfficiency: 15, trafficFactor: 1.2,
    timeWindowFlexibility: 0.5, graphHopperProfile: 'car', osmWeighting: 'fastest'
  });

  // ===== ALGORITHM LOGIC HANDLERS ===== //
  const handleSaveAlgorithmConfig = (config) => {
    setPriorityConfig(config.priority);
    setVehicleTypes(config.allowedTypes);
    setAlgorithmParams(config.params);
    alert('Algorithm configuration saved successfully!');
  };

  const handleResetAlgorithmConfig = () => {
    setPriorityConfig(['bike', 'car', 'truck']);
    setVehicleTypes(['bike', 'car', 'truck']);
    setAlgorithmParams({
      maxDistance: 500, maxTime: 8, maxStops: 20, costPerKM: 10, fuelEfficiency: 15, trafficFactor: 1.2,
      timeWindowFlexibility: 0.5, graphHopperProfile: 'car', osmWeighting: 'fastest'
    });
  };

  // ===== API CALL WITH DYNAMIC PARAMETERS ===== //
  const callOptimizationAPI = async () => {
    try {
      setApiError('');
      const requestPayload = {
        Deliveries: deliveries.map(d => ({
          Id: d.id, Name: d.name, Location: d.location, Priority: d.priority, Weight: d.weight,
          Latitude: d.coords[1], Longitude: d.coords[0], TimeWindow: { Start: d.timeWindow.start, End: d.timeWindow.end }
        })),
        Vehicles: vehicles.map(v => ({
          Id: v.id, Name: v.name, Type: v.type.toLowerCase(), Driver: v.driver, Capacity: v.capacity,
          Location: v.location, Status: v.status, Latitude: v.coords[1], Longitude: v.coords[0],
          Profile: algorithmParams.graphHopperProfile
        })),
        VehiclePriority: priorityConfig,
        AllowedVehicleTypes: vehicleTypes,
        AlgorithmParams: algorithmParams
      };

      const response = await fetch(`${API_BASE_URL}/RouteOptimization/optimize`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'API request failed');
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      setApiError(error.message);
      throw error;
    }
  };

  // ===== EXISTING FUNCTIONS ===== //
  const updateMetricsFromAPI = (apiResponse) => {
    setMetrics({
      totalDistance: `${apiResponse.metrics.totalDistance.toFixed(2)} km`,
      totalTime: `${Math.floor(apiResponse.metrics.totalTime)} hrs ${Math.round((apiResponse.metrics.totalTime % 1) * 60)} mins`,
      totalCost: `₹${apiResponse.metrics.totalCost}`,
      vehiclesUsed: `${apiResponse.metrics.vehiclesUsed}/${apiResponse.metrics.totalVehicles}`,
      optimization: `↓ ${apiResponse.metrics.resourceOptimization}% resource optimization`,
    });
  };

  const transformAPIRoutes = (apiResponse) => {
    return apiResponse.routes.map((route) => ({
      vehicle: {
        id: route.vehicle.id, name: route.vehicle.name,
        type: route.vehicle.type.toLowerCase(),
        driver: route.vehicle.driver || "Driver", capacity: route.vehicle.capacity || 500,
        coords: [route.vehicle.longitude, route.vehicle.latitude],
      },
      deliveries: route.deliveries.map((delivery) => ({
        id: delivery.id, name: delivery.name, location: delivery.location,
        priority: delivery.priority || "medium", weight: delivery.weight || 100,
        coords: [delivery.longitude, delivery.latitude],
        timeWindow: delivery.timeWindow || { start: 9, end: 17 },
      })),
      totalWeight: route.totalWeight || 0,
      totalDistance: route.totalDistance || 0,
      totalTime: route.totalTime || 0,
      totalTimeMinutes: route.totalTimeMinutes || 0,
      totalCost: route.totalCost || 0,
      routeSequence: route.routeSequence || [],
    }));
  };

  const startOptimization = async () => {
    const availableVehicles = vehicles.filter(v => v.status === 'available');
    if (availableVehicles.length === 0) {
      alert('Please make at least one vehicle available for optimization.');
      return;
    }

    setIsOptimizing(true);
    setOptimizationStep(0);
    setShowOptimizedRoutes(false);
    setApiError('');

    const interval = setInterval(() => {
      setOptimizationStep(prev => (prev < 5 ? prev + 1 : prev));
    }, 1000);

    try {
      const requestPayload = {
        Deliveries: deliveries.map(d => ({
          Id: d.id, Name: d.name, Location: d.location, Priority: d.priority, Weight: d.weight,
          Latitude: d.coords[1], Longitude: d.coords[0], TimeWindow: { Start: d.timeWindow.start, End: d.timeWindow.end }
        })),
        Vehicles: vehicles.map(v => ({
          Id: v.id, Name: v.name, Type: v.type.toLowerCase(), Driver: v.driver, Capacity: v.capacity,
          Location: v.location, Status: v.status, Latitude: v.coords[1], Longitude: v.coords[0],
          Profile: algorithmParams.graphHopperProfile
        })),
        VehiclePriority: priorityConfig,
        AllowedVehicleTypes: vehicleTypes,
        AlgorithmParams: algorithmParams
      };

      const response = await fetch(`${API_BASE_URL}/RouteOptimization/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'API request failed');
      }

      const apiResponse = await response.json();
      clearInterval(interval);
      setOptimizationStep(5);
      const transformedRoutes = transformAPIRoutes(apiResponse);
      setOptimizedRoutes(transformedRoutes);
      updateMetricsFromAPI(apiResponse);

      // Generate short URLs for each route
      const newShortURLs = {};
      transformedRoutes.forEach((route, index) => {
        const shortURL = generateShortURL(route);
        newShortURLs[`route_${index}`] = shortURL;
      });
      setShortURLs(newShortURLs);

      setTimeout(() => { setShowOptimizedRoutes(true); setIsOptimizing(false); }, 500);
    } catch (error) {
      clearInterval(interval);
      setIsOptimizing(false);
      setOptimizationStep(0);
      alert(`Optimization failed: ${error.message}\n\nPlease ensure the API is running at ${API_BASE_URL}`);
    }
  };

  const resetOptimization = () => {
    setOptimizationStep(0);
    setShowOptimizedRoutes(false);
    setOptimizedRoutes([]);
    setApiError('');
  };

  const addDelivery = async (formData) => {
    const newDelivery = {
      id: deliveries.length > 0 ? Math.max(...deliveries.map(d => d.id)) + 1 : 1,
      name: formData.name, location: formData.location, priority: formData.priority,
      time: `${formData.timeStart}:00`, weight: formData.weight,
      coords: [formData.longitude, formData.latitude],
      timeWindow: { start: formData.timeStart, end: formData.timeEnd }
    };
    setDeliveries([...deliveries, newDelivery]);
    setShowDeliveryModal(false);
    resetOptimization();
  };

  const addVehicle = async (formData) => {
    const newVehicle = {
      id: vehicles.length > 0 ? Math.max(...vehicles.map(v => v.id)) + 1 : 1,
      name: formData.name, type: formData.type.toLowerCase(), driver: formData.driver,
      capacity: formData.capacity, location: formData.location, status: 'available',
      coords: [formData.longitude, formData.latitude], profile: formData.type
    };
    setVehicles([...vehicles, newVehicle]);
    setShowVehicleModal(false);
    resetOptimization();
  };

  const removeDelivery = (id) => {
    if (window.confirm('Are you sure you want to remove this delivery?')) {
      setDeliveries(deliveries.filter(d => d.id !== id));
      resetOptimization();
    }
  };

  const removeVehicle = (id) => {
    if (vehicles.length <= 1) {
      alert('Cannot remove the last vehicle! At least one vehicle is required.');
      return;
    }
    if (window.confirm('Are you sure you want to remove this vehicle?')) {
      setVehicles(vehicles.filter(v => v.id !== id));
      resetOptimization();
    }
  };

  const toggleVehicle = (id) => {
    setVehicles(vehicles.map(v => v.id === id ? { ...v, status: v.status === 'available' ? 'unavailable' : 'available' } : v));
    resetOptimization();
  };

  // ===== EDIT FUNCTIONS ===== //
  const editDelivery = (delivery) => {
    setEditingDelivery(delivery);
    setShowEditDeliveryModal(true);
  };

  const editVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowEditVehicleModal(true);
  };

  const updateDelivery = (updatedDelivery) => {
    setDeliveries(deliveries.map(d =>
      d.id === updatedDelivery.id ? {
        ...d,
        name: updatedDelivery.name,
        location: updatedDelivery.location,
        weight: updatedDelivery.weight,
        priority: updatedDelivery.priority,
        timeWindow: { start: updatedDelivery.timeStart, end: updatedDelivery.timeEnd },
        coords: [updatedDelivery.longitude, updatedDelivery.latitude]
      } : d
    ));
    setShowEditDeliveryModal(false);
    resetOptimization();
  };

  const updateVehicle = (updatedVehicle) => {
    setVehicles(vehicles.map(v =>
      v.id === updatedVehicle.id ? {
        ...v,
        name: updatedVehicle.name,
        type: updatedVehicle.type,
        driver: updatedVehicle.driver,
        capacity: updatedVehicle.capacity,
        location: updatedVehicle.location,
        coords: [updatedVehicle.longitude, updatedVehicle.latitude]
      } : v
    ));
    setShowEditVehicleModal(false);
    resetOptimization();
  };

  const availableVehicles = vehicles.filter(v => v.status === 'available');
  const totalCapacity = availableVehicles.reduce((sum, v) => sum + v.capacity, 0);
  const totalWeight = deliveries.reduce((sum, d) => sum + d.weight, 0);

  let warningMessage = '';
  if (availableVehicles.length === 0) {
    warningMessage = 'No vehicles available! Please make at least one vehicle available for optimization.';
  } else if (totalWeight > totalCapacity) {
    warningMessage = `Warning: Total delivery weight (${totalWeight}kg) exceeds available capacity (${totalCapacity}kg). Some deliveries may not be assigned.`;
  } else if (availableVehicles.length < vehicles.length) {
    warningMessage = `${vehicles.length - availableVehicles.length} vehicle(s) unavailable. Workload will be redistributed among ${availableVehicles.length} available vehicle(s).`;
  }

  useEffect(() => {
    if (selectedRoute) {
      setLoadingInstructions(true);
      const points = [
        { latitude: selectedRoute.vehicle.coords[1], longitude: selectedRoute.vehicle.coords[0] },
        ...selectedRoute.deliveries.map(delivery => ({ latitude: delivery.coords[1], longitude: delivery.coords[0] })),
        { latitude: selectedRoute.vehicle.coords[1], longitude: selectedRoute.vehicle.coords[0] },
      ];
      fetchDirections(points)
        .then(instructions => { setInstructions(instructions); setLoadingInstructions(false); })
        .catch(() => { setInstructions([]); setLoadingInstructions(false); });
    }
  }, [selectedRoute]);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .custom-tooltip {
        background-color: rgba(0, 0, 0, 0.85); color: white; border: 1px solid #fff; border-radius: 6px;
        font-size: 14px; padding: 10px; max-width: 300px; line-height: 1.4;
      }
      .custom-tooltip::before { border-bottom-color: rgba(0, 0, 0, 0.85); }
    `;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px 40px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1400px', margin: '0 auto' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="3" width="15" height="13"></rect>
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
        <circle cx="5.5" cy="18.5" r="2.5"></circle>
        <circle cx="18.5" cy="18.5" r="2.5"></circle>
      </svg>
      <div>
        <h1 style={{ fontSize: '28px', fontWeight: 600, margin: 0 }}>Route Optimization System</h1>
        <p style={{ fontSize: '14px', opacity: 0.9, marginTop: '5px', margin: 0 }}>Intelligent Delivery Management - API Integrated</p>
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <button
        onClick={() => {
          localStorage.removeItem('isAuthenticated');
          window.location.href = '/';
        }}
        style={{
          padding: '8px 16px',
          background: '#e74c3c',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        Logout
      </button>
      <span style={{ fontSize: '12px', padding: '4px 10px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px' }}>
        {apiError ? '❌ API Error' : '🟢 API Ready'}
      </span>
    </div>
  </div>
</div>


      {/* Navigation Tabs */}
      <div style={{ background: 'white', borderBottom: '2px solid #e5e7eb', padding: '0 40px' }}>
        <div style={{ display: 'flex', gap: '5px', maxWidth: '1400px', margin: '0 auto' }}>
          {[
            { id: 'dashboard', label: '📊 Dashboard' },
            { id: 'deliveries', label: '📦 Deliveries' },
            { id: 'vehicles', label: '🚚 Vehicles' },
            { id: 'optimization', label: '🎯 Optimization' },
            { id: 'algorithm', label: '⚙️ Algorithm Logic' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '15px 25px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '15px',
                color: activeTab === tab.id ? '#667eea' : '#6b7280',
                borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent',
                fontWeight: activeTab === tab.id ? 600 : 400
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 40px' }}>
        {apiError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <strong>Error:</strong> {apiError}
              <br />
              <small>Internal Server Error Please Try again later!</small>
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && <DashboardTab metrics={metrics} optimizedRoutes={optimizedRoutes} shortURLs={shortURLs} />}
        {activeTab === 'deliveries' && <DeliveriesTab deliveries={deliveries} onAddDelivery={() => setShowDeliveryModal(true)} onRemoveDelivery={removeDelivery} onEditDelivery={editDelivery} />}
        {activeTab === 'vehicles' && <VehiclesTab vehicles={vehicles} onAddVehicle={() => setShowVehicleModal(true)} onRemoveVehicle={removeVehicle} onToggleVehicle={toggleVehicle} onEditVehicle={editVehicle} />}
        {activeTab === 'optimization' && (
          <OptimizationTab
            warningMessage={warningMessage} optimizationStep={optimizationStep} isOptimizing={isOptimizing}
            showOptimizedRoutes={showOptimizedRoutes} optimizedRoutes={optimizedRoutes}
            onStartOptimization={startOptimization} onResetOptimization={resetOptimization}
            onViewRouteDetails={(route) => { setSelectedRoute(route); setShowRouteAnalysisModal(true); }}
          />
        )}
        {activeTab === 'algorithm' && (
          <AlgorithmLogicTab
            vehicleTypes={vehicleTypes} priorityConfig={priorityConfig} algorithmParams={algorithmParams}
            onSaveConfig={handleSaveAlgorithmConfig} onResetConfig={handleResetAlgorithmConfig}
          />
        )}
      </div>

      {/* Modals */}
      {showDeliveryModal && <DeliveryModal onClose={() => setShowDeliveryModal(false)} onSubmit={addDelivery} getCoordinates={getCoordinatesFromGoogle} />}
      {showVehicleModal && <VehicleModal onClose={() => setShowVehicleModal(false)} onSubmit={addVehicle} getCoordinates={getCoordinatesFromGoogle} />}
      {showRouteAnalysisModal && <RouteAnalysisModal route={selectedRoute} instructions={instructions} loading={loadingInstructions} onClose={() => setShowRouteAnalysisModal(false)} />}
      {showEditDeliveryModal && <EditDeliveryModal delivery={editingDelivery} onClose={() => setShowEditDeliveryModal(false)} onSubmit={updateDelivery} getCoordinates={getCoordinatesFromGoogle} />}
      {showEditVehicleModal && <EditVehicleModal vehicle={editingVehicle} onClose={() => setShowEditVehicleModal(false)} onSubmit={updateVehicle} getCoordinates={getCoordinatesFromGoogle} />}
    </div>
  );
};

// Export both components for routing
export default RouteOptimizationSystem;
